import React, { createContext, forwardRef, useContext, useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LineChart } from "@mui/x-charts/LineChart";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { ChartsTooltipContainer, useAxesTooltip } from "@mui/x-charts/ChartsTooltip";
import { BENCHMARK, STATUS, TREND_WORD, countText, longMonth, shortMonth } from "./trendDisplay";

const TooltipData = createContext(null);

// Mirrors the app's axis tooltip: the month, the domain's value to two decimals and who contributed.
function TrendTooltipContent() {
    const data = useContext(TooltipData);
    const axes = useAxesTooltip();
    if (!data || !axes || !axes.length) return null;
    const index = axes[0].dataIndex;
    const value = data.domain.values[index];
    const month = data.months[index];
    const count = data.domain.counts[index];
    let foot = "No observation recorded";
    if (month?.suppressed) foot = `Fewer than ${data.minParticipants} participants`;
    else if (value != null && count > 0) foot = countText(count, data.countLabel);
    return (
        <Box
            sx={{
                minWidth: 150,
                p: "8px 12px",
                borderRadius: 1,
                bgcolor: "background.paper",
                border: "1px solid rgba(0,0,0,0.12)",
                boxShadow: 3,
                fontSize: 14,
                color: "rgba(0,0,0,0.87)",
            }}
        >
            <Box sx={{ color: "rgba(0,0,0,0.6)", pb: 0.75, mb: 0.75, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
                {month ? longMonth(month.key) : ""}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: "2px", bgcolor: data.color, flexShrink: 0 }} />
                <span>{data.domain.name}</span>
                <Box component="strong" sx={{ ml: "auto", pl: 1.5, fontWeight: 500 }}>
                    {value == null ? "–" : Number(value).toFixed(2)}
                </Box>
            </Box>
            <Box sx={{ mt: 0.5, fontSize: 12, color: "rgba(0,0,0,0.6)" }}>{foot}</Box>
        </Box>
    );
}

function TrendTooltip(props) {
    return (
        <ChartsTooltipContainer {...props} trigger="axis">
            <TrendTooltipContent />
        </ChartsTooltipContainer>
    );
}

function subtitle(assessment) {
    if (!assessment || assessment.status === "none") return "No observations in this period";
    return (
        <>
            <Box component="strong" sx={{ color: "#141414", fontSize: 14, fontWeight: 600 }}>
                {assessment.latest.text}
            </Box>{" "}
            / 5 in {shortMonth(assessment.latest.month)} · {TREND_WORD[assessment.trend] || ""}
        </>
    );
}

// One domain's monthly averages, drawn like the app's check-in trend chart (area: true, monotoneX, no marks)
// and coloured by the domain's status. The dashed line is the 3.2 benchmark.
const DomainTrendChart = forwardRef(function DomainTrendChart(
    { domain, months, countLabel, minParticipants, highlighted, dimmed },
    ref
) {
    const assessment = domain.assessment;
    const status = STATUS[assessment?.status] || STATUS.none;
    const labels = useMemo(() => months.map((m) => shortMonth(m.key)), [months]);
    const values = domain.values;
    // A month with no neighbour has no line to show, so it gets a dot.
    const isolated = (index) =>
        values[index] != null && values[index - 1] == null && values[index + 1] == null;
    // Label every month while there is room (about 30px each in a ~130px plot), otherwise every second or third.
    const stride = Math.max(1, Math.ceil((30 * (months.length - 1)) / 130));
    const info = `Average ${domain.name} score for each month, out of 5. The dashed line marks the 3.2 benchmark.`;
    const tooltipData = useMemo(
        () => ({ domain, months, countLabel, minParticipants, color: status.color }),
        [domain, months, countLabel, minParticipants, status.color]
    );
    const hasData = assessment?.status && assessment.status !== "none";

    return (
        <Box
            ref={ref}
            component="section"
            aria-label={`${domain.name} monthly trend`}
            data-domain={domain.id}
            data-status={assessment?.status || "none"}
            data-highlight={highlighted ? "true" : "false"}
            sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: highlighted ? status.ink : "#CBD5E1",
                boxShadow: highlighted ? `0 0 0 1px ${status.ink}` : "none",
                bgcolor: "#fff",
                opacity: dimmed ? 0.45 : 1,
                transition: "border-color .18s, box-shadow .18s, opacity .18s",
                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.75, minWidth: 0 }}>
                <Typography
                    component="h3"
                    sx={{ color: "#141414", fontSize: 16, fontWeight: 600, lineHeight: "24px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                    {domain.name}
                </Typography>
                <Tooltip title={info} arrow placement="top">
                    <InfoOutlinedIcon tabIndex={0} aria-label={info} sx={{ fontSize: 16, color: "#64748B", cursor: "help", flexShrink: 0 }} />
                </Tooltip>
            </Box>
            <Typography sx={{ color: "#64748B", fontSize: 12, lineHeight: "16px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {subtitle(assessment)}
            </Typography>
            <Box
                component="span"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    alignSelf: "flex-start",
                    height: 24,
                    px: 1.25,
                    borderRadius: 4,
                    border: `1px solid ${status.color}`,
                    bgcolor: status.background,
                    color: status.ink,
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                }}
            >
                {status.label}
            </Box>
            <Box sx={{ position: "relative", height: 190, mx: -0.5 }}>
                <TooltipData.Provider value={tooltipData}>
                    <LineChart
                        height={190}
                        margin={{ top: 20, right: 16, bottom: 0, left: 0 }}
                        hideLegend
                        xAxis={[
                            {
                                data: labels,
                                scaleType: "point",
                                height: 24,
                                tickLabelInterval: (value, index) => (months.length - 1 - index) % stride === 0,
                                tickLabelStyle: { fontSize: 12 },
                            },
                        ]}
                        yAxis={[{ min: 0, max: 5, width: 28, tickInterval: [0, 1, 2, 3, 4, 5], tickLabelStyle: { fontSize: 12 } }]}
                        series={[
                            {
                                data: values,
                                label: domain.name,
                                color: status.color,
                                area: true,
                                curve: "monotoneX",
                                connectNulls: false,
                                showMark: ({ index }) => isolated(index),
                                valueFormatter: (value) => (value == null ? "–" : Number(value).toFixed(2)),
                            },
                        ]}
                        slots={{ tooltip: TrendTooltip }}
                        sx={{
                            "& .MuiMarkElement-root": { fill: status.color, stroke: status.color },
                            "& .MuiChartsOverlay-root": { display: "none" },
                        }}
                    >
                        <ChartsReferenceLine
                            y={BENCHMARK}
                            lineStyle={{ stroke: "#475569", strokeDasharray: "4 3", shapeRendering: "crispEdges" }}
                        />
                    </LineChart>
                </TooltipData.Provider>
                {!hasData && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: "0 0 30px 30px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            color: "#64748B",
                            fontSize: 13,
                            fontWeight: 500,
                            px: 1,
                            pointerEvents: "none",
                        }}
                    >
                        No data available for this period
                    </Box>
                )}
            </Box>
        </Box>
    );
});

export default DomainTrendChart;
